import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { api, errMessage } from "../../lib/api";

const TIER_LABELS = {
  celebrate: "Strong try", almost: "Getting close", listen: "Listened & tried", "": "Recorded try",
};

// Parent review & delete controls for stored speech-engine score records.
// Records exist only with progress consent; audio itself is NEVER stored.
// "Delete all" also rotates the child's anonymous speaker token.
export const SpeechRecords = ({ childId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const load = () => api.get(`/speech/children/${childId}/attempt-scores`)
    .then(({ data: d }) => setData(d)).catch(() => setData({ error: true }));
  useEffect(() => { if (childId) load(); }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!childId || !data) return null;
  if (data.error) {
    return (
      <p className="text-[10px] text-muted-foreground border-t border-border pt-3"
        data-testid="speech-records-error">
        Couldn't load practice score records right now — please refresh.
      </p>
    );
  }

  const removeOne = async (attemptId) => {
    try {
      await api.delete(`/speech/children/${childId}/attempt-scores/${attemptId}`);
      toast.success("Record deleted");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const removeAll = async () => {
    if (!window.confirm("Delete every stored practice score record for this child?")) return;
    try {
      const { data: d } = await api.delete(`/speech/children/${childId}/attempt-scores`);
      toast.success(`${d.deleted} record${d.deleted === 1 ? "" : "s"} deleted`);
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <div className="border-t border-border pt-3 space-y-2" data-testid="speech-records">
      <button type="button" onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-bold w-full"
        data-testid="speech-records-toggle">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
        Practice score records ({data.total})
        {open ? <ChevronUp className="h-3.5 w-3.5 ml-auto" aria-hidden="true" />
              : <ChevronDown className="h-3.5 w-3.5 ml-auto" aria-hidden="true" />}
      </button>
      {open && (
        <div className="space-y-2" data-testid="speech-records-list">
          <p className="text-[10px] text-muted-foreground">
            Voice practice consent: <b>{data.consent.voice_practice ? "ON" : "OFF"}</b> ·
            score saving (progress): <b>{data.consent.progress_tracking ? "ON" : "OFF"}</b>.
            Only sound scores are kept — recordings are never stored. Delete anything, any time.
          </p>
          {data.records.map((r) => (
            <div key={r.attempt_id}
              className="flex items-center justify-between gap-2 rounded-lg border border-border p-2"
              data-testid={`speech-record-${r.attempt_id}`}>
              <div className="min-w-0 text-[11px]">
                <b>“{r.target}”</b> — {TIER_LABELS[r.tier] || r.tier}
                {r.heard ? <> · heard: “{r.heard}”</> : null}
                <span className="text-muted-foreground"> · {(r.created_at || "").slice(0, 10)}</span>
              </div>
              <button type="button" title="Delete this record" onClick={() => removeOne(r.attempt_id)}
                className="rounded-full border border-border p-1.5 hover:bg-muted text-red-600 shrink-0"
                data-testid={`speech-record-delete-${r.attempt_id}`}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          ))}
          {data.records.length === 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="speech-records-empty">
              No stored score records for this child.
            </p>
          )}
          {data.total > 0 && (
            <Button size="sm" variant="outline" onClick={removeAll}
              className="rounded-full text-[11px] h-7 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="speech-records-delete-all">
              Delete all records
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
